/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GridOptions, IRowNode, ProcessDataFromClipboardParams } from 'ag-grid-community';
import { useCallback } from 'react';
import { isDataRow } from './captable-utils';

export function useEditableGridDefinitions() {
  // might require small changes when we upgrade to latest version of ag-grid
  // (see https://www.ag-grid.com/react-data-grid/clipboard/#pasting-new-rows-at-the-bottom-of-the-grid)
  const processDataFromClipboard = useCallback(
    (params: ProcessDataFromClipboardParams): string[][] | null => {
      // Pinned rows are read-only; pasting anchored there would silently drop the first row.
      if (params.api.getFocusedCell()?.rowPinned) {
        return null;
      }
      const data = [...params.data];
      const emptyLastRow = data[data.length - 1][0] === '' && data[data.length - 1].length === 1;
      if (emptyLastRow) {
        data.splice(data.length - 1, 1);
      }
      const focusedCell = params.api.getFocusedCell();
      const focusedIndex = focusedCell!.rowIndex;
      // The grand-total footer counts as a displayed row but is not a paste target (ag-grid skips
      // footers when pasting), so size the grid by the data rows at/below the focus instead.
      const displayedRowCount = params.api.getDisplayedRowCount();
      let numPasteTargets = 0;
      for (let i = focusedIndex; i < displayedRowCount; i++) {
        const node = params.api.getDisplayedRowAtIndex(i);
        if (node && isDataRow(node)) {
          numPasteTargets += 1;
        }
      }
      const numRowsToAdd = data.length - numPasteTargets;
      if (numRowsToAdd > 0) {
        const rowsToAdd: any[] = [];
        for (let i = 0; i < numRowsToAdd; i++) {
          const index = data.length - 1;
          const row = data.slice(index, index + 1)[0];
          // Create row object
          const rowObject: any = {};
          let currentColumn: any = focusedCell!.column;
          row.forEach((item) => {
            if (!currentColumn) {
              return;
            }
            rowObject[currentColumn.colDef.field] = item;
            currentColumn = params.api.getDisplayedColAfter(currentColumn);
          });
          rowsToAdd.push(rowObject);
        }
        params.api.applyTransaction({ add: rowsToAdd });
      }
      return data;
    },
    []
  );

  return { processDataFromClipboard };
}
export const EditableGridDefaultColDefs: ColDef = {
  editable: true,
  flex: 1,
  minWidth: 150,
};

export const EditableGridOptions: GridOptions = {
  rowSelection: {
    checkboxes: true,
    enableClickSelection: false,
    mode: 'multiRow',
    // Prevent footer/total rows from being selectable
    isRowSelectable: (node: IRowNode) => isDataRow(node),
  },
  suppressClipboardPaste: false,
};
